import {TZDate} from '@date-fns/tz'
import {DotLottieReact} from '@lottiefiles/dotlottie-react'
import Big from 'big.js'
import {addMinutes, formatRelative} from 'date-fns'
import {Breadcrumb, Breadcrumbs} from 'react-aria-components'
import {capitalize} from 'remeda'
import zipToTz from 'zip-to-tz'

import ClockSvg from '@/assets/icons/clock.svg?react'
import EditSvg from '@/assets/icons/edit.svg?react'
import TrashSvg from '@/assets/icons/trash.svg?react'
import spinnerLottie from '@/assets/lotties/spinner.lottie'
import ProductPrice from '@/components/ProductCard/ProductPrice'
import QuantitySelector from '@/components/QuantitySelector/QuantitySelector'
import SkipLink from '@/components/SkipLink'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import {type FoodCartItem, type FoodProduct} from '@/lib/food/foodModel'
import {useFoodCartFull} from '@/lib/food/hooks/useFoodCartFull'
import useFoodCartMutation, {FoodCartAction} from '@/lib/food/hooks/useFoodCartMutation'
import useFoodCartProductInfoQueries from '@/lib/food/hooks/useFoodCartProductInfoQueries'
import useStoreDetails from '@/lib/food/hooks/useStoreDetails'
import {useScheduledId, useUserAddress} from '@/lib/food/store'
import HeadTags from '@/lib/head/HeadTags'
import {useLocale} from '@/lib/locale/locale'
import {FoodCartRoute, FoodRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {useIsMobile} from '@/utils/hooks/useWindowSize'
import RACLink from '@/utils/router/RACLink'

import style from './FoodCart.module.scss'
import FoodCartSummary from './FoodCartSummary'

function buildOptionsText(
  foodCartItem: FoodProduct | undefined,
  selectedOptions: FoodCartItem['selectedOptions'] | undefined,
) {
  const texts: string[] = []

  const selectedOptionsMap = new Map(selectedOptions?.map(option => [option.optionId, option]))

  function traverseCustomizations(
    customizations: FoodProduct['customizations'] | undefined,
    toAppend: string[],
  ) {
    customizations?.forEach(customization => {
      const texts2: string[] = []
      customization.options?.forEach(option => {
        const selectedOption = selectedOptionsMap.get(option.option_id)
        if (selectedOption && selectedOption.quantity !== 0) {
          texts2.push(`${option.name} * ${selectedOption.quantity}`)
        }

        if (option.customizations) {
          traverseCustomizations(option.customizations, texts2)
        }
      })

      if (texts2.length > 0) {
        toAppend.push(`${customization.name} (${texts2.join(', ')})`)
      }
    })
  }

  traverseCustomizations(foodCartItem?.customizations, texts)

  return texts.join(', ')
}

function getPrice(
  foodCartItem: FoodProduct | undefined,
  selectedOptions: FoodCartItem['selectedOptions'] | undefined,
) {
  let price = Big(foodCartItem?.price ?? 0).div(100)

  const selectedOptionsMap = new Map(selectedOptions?.map(option => [option.optionId, option]))

  function traverseCustomizations(customizations: FoodProduct['customizations'] | undefined) {
    customizations?.forEach(customization => {
      customization.options?.forEach(option => {
        const selectedOption = selectedOptionsMap.get(option.option_id)
        if (selectedOption && selectedOption.quantity !== 0) {
          price = price.plus(Big(option.price).div(100).times(selectedOption.quantity))
        }

        if (option.customizations) {
          traverseCustomizations(option.customizations)
        }
      })
    })
  }

  traverseCustomizations(foodCartItem?.customizations)

  return price
}

const FoodCartItemComponent = memo(function FoodCartItemComponent({
  item,
  isMobile,
  storeId,
}: {
  item: FoodCartItem & {details: FoodProduct}
  isMobile: boolean
  storeId: string
}) {
  const latestItem = useLatest(item)
  const latestStoreId = useLatest(storeId)

  const selectedOptionsText = buildOptionsText(item.details, item.selectedOptions)
  const price = getPrice(item.details, item.selectedOptions)

  const {data: storeDetails} = useStoreDetails(storeId)

  const latestCurrentSelectedOptions = useLatest(item.selectedOptions)

  const {mutate: mutateFoodCart} = useFoodCartMutation()

  const handleQuantityChange = useCallback(
    (q: number) => {
      mutateFoodCart({
        action: FoodCartAction.UpdateQuantity,
        data: {
          item: {
            productId: latestItem.current.productId,
            selectedOptions: latestCurrentSelectedOptions.current,
            storeId: latestStoreId.current,
          },
          quantity: q,
        },
      })
    },
    [mutateFoodCart],
  )

  const handleRemoveItem = useCallback(() => {
    mutateFoodCart({
      action: FoodCartAction.Remove,
      data: {
        item: {
          productId: latestItem.current.productId,
          selectedOptions: latestCurrentSelectedOptions.current,
          storeId: latestStoreId.current,
        },
      },
    })
  }, [mutateFoodCart])

  if (isMobile) {
    return (
      <>
        <tr key={item.productId}>
          {/* eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role -- this is okay */}
          <td role='presentation'>
            <div className={style.Item}>
              <div aria-hidden>
                {item.details.thumbnail_image && (
                  <img src={item.details.thumbnail_image} alt={item.details.name} />
                )}
              </div>
              <div className={style.ItemInfo}>
                <div role='cell'>
                  <VisuallyHidden as='div'>
                    {item.details.thumbnail_image && (
                      <img src={item.details.thumbnail_image} alt={item.details.name} />
                    )}
                  </VisuallyHidden>
                  <div className={style.ItemName}>{item.details.name}</div>
                  <div className={style.VariantOption}>
                    <div>{selectedOptionsText}</div>
                    {item.notes && <div>Notes: {item.notes}</div>}
                  </div>
                </div>
                <VisuallyHidden role='cell'>{storeDetails?.name}</VisuallyHidden>
                <div aria-hidden>
                  <ProductPrice price={price.times(item.quantity).toNumber()} />
                </div>
                <div role='cell'>
                  <QuantitySelector
                    allowZero
                    quantity={item.quantity}
                    setQuantity={handleQuantityChange}
                  />
                </div>
                <VisuallyHidden role='cell'>
                  <ProductPrice price={price.times(item.quantity).toNumber()} />
                </VisuallyHidden>
                <div role='cell' className={style.ItemRemove}>
                  <button
                    className={style.RemoveItemBtn}
                    onClick={handleRemoveItem}
                    aria-label={`Remove ${item.details.name} from cart`}
                  >
                    <TrashSvg className={style.TrashIcon} />
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
        {/* <VisuallyHidden as='tr' strict>
          <td>
            <TRLink
              to='/product/$productId'
              params={{
                productId: {
                  retailer: item.retailer,
                  id: item.id,
                },
              }}
            >
              <img src={item.image} alt={item.title} />
            </TRLink>
            <TRLink
              to='/product/$productId'
              params={{
                productId: {
                  retailer: item.retailer,
                  id: item.id,
                },
              }}
            >
              {item.title}
            </TRLink>
          </td>
          <td>{item.seller ?? item.retailer}</td>
          <td>
            <QuantitySelector
              allowZero
              quantity={item.quantity}
              setQuantity={handleQuantityChange}
            />
          </td>
          <td>
            <ProductPrice price={item.price * item.quantity} />
          </td>
          <td>
            <button
              className={style.RemoveItemBtn}
              onClick={handleRemoveItem}
              aria-label={`Remove ${item.title} from cart`}
            >
              <TrashSvg className={style.TrashIcon} />
            </button>
          </td>
        </VisuallyHidden> */}
      </>
    )
  }

  return (
    <tr key={item.productId}>
      <td>
        <div className={style.Item}>
          <div>
            {item.details.thumbnail_image && (
              <img src={item.details.thumbnail_image} alt={item.details.name} />
            )}
          </div>
          <div className={style.ItemInfo}>
            <div className={style.ItemName}>{item.details.name}</div>
            <div className={style.VariantOption}>
              <div>{selectedOptionsText}</div>
              {item.notes && <div>Notes: {item.notes}</div>}
            </div>
          </div>
        </div>
      </td>
      <VisuallyHidden as='td'>{storeDetails?.name}</VisuallyHidden>
      <td>
        <QuantitySelector allowZero quantity={item.quantity} setQuantity={handleQuantityChange} />
      </td>
      <td>
        <ProductPrice price={price.times(item.quantity).toNumber()} />
      </td>
      <td>
        <button
          className={style.RemoveItemBtn}
          onClick={handleRemoveItem}
          aria-label={`Remove ${item.details.name} from cart`}
        >
          <TrashSvg className={style.TrashIcon} />
        </button>
      </td>
    </tr>
  )
})

export default memo(function Cart() {
  const isMobile = useIsMobile()
  const [locale] = useLocale()

  const {results, isLoading} = useFoodCartProductInfoQueries(locale)

  const storeId = results.foodCartItems[0]?.storeId

  const {data: storeDetails} = useStoreDetails(storeId)

  const cartItemNumber = results.foodCartItems.length

  const {coupon, applyRewards} = FoodCartRoute.useSearch()

  const isEmpty = !isLoading && cartItemNumber === 0

  const {data: foodCart, error} = useFoodCartFull(locale)
  console.log(error)

  const [userAddress] = useUserAddress()

  const timezone = zipToTz(userAddress.zipcode)

  const [scheduleId] = useScheduledId()

  const userAddressString =
    [
      userAddress.street_addr,
      userAddress.street_addr_2,
      userAddress.city,
      userAddress.state,
      userAddress.zipcode,
      userAddress.country,
    ]
      .filter(Boolean)
      .join(', ') || 'None'

  const expectedDeliveryMinute = foodCart
    ? Math.round(
        (foodCart.final_quote.quote.delivery_time_min +
          foodCart.final_quote.quote.delivery_time_max) /
          2,
      )
    : 0

  const expectedDeliveryDate = addMinutes(new TZDate(new Date(), timezone), expectedDeliveryMinute)
  const nowTZ = new TZDate(new Date(), timezone)

  const now = new Date(`${nowTZ.getFullYear()}-${nowTZ.getMonth() + 1}-${nowTZ.getDate()}`)

  const expectedDeliveryDateRelative = capitalize(
    formatRelative(expectedDeliveryDate.toLocaleString(), now),
  )

  return (
    <>
      <HeadTags title='Food Cart' />
      <SkipLink title='Skip to main content' to='#main-content' />
      <div className={style.Page}>
        <Navbar
          transparentBg
          withoutLogoOnDesktop
          variant='light'
          className={style.Navbar}
          haveCart={false}
        />
        <main className={style.CartPage} {...skipTargetProps('main-content')}>
          <div className={style.CartContent}>
            <div className={style.BreadcrumbsContainer} role='region' aria-label='breadcrumb'>
              <Breadcrumbs className={style.Breadcrumbs}>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink
                    className={style.Link}
                    to={FoodRoute.fullPath}
                    activeOptions={{
                      exact: true,
                    }}
                  >
                    Home
                  </RACLink>
                </Breadcrumb>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink
                    className={style.Link}
                    to={FoodCartRoute.fullPath}
                    search={{
                      applyRewards,
                      coupon,
                    }}
                  >
                    Food Cart
                  </RACLink>
                </Breadcrumb>
              </Breadcrumbs>
            </div>
            <h1 className={style.Heading}>Your food cart</h1>
            <div className={style.DeliveryInfo}>
              Delivery to: <span>{userAddressString}</span>
              <Link to={FoodRoute.fullPath} aria-label='Update delivery address'>
                <EditSvg />
              </Link>
            </div>
            <div className={style.Cart}>
              <div className={style.CartItems}>
                <table className={style.CartTable}>
                  <caption>{`All items (${cartItemNumber} items)`}</caption>
                  <VisuallyHidden as='thead' isVisible={!isMobile}>
                    <tr>
                      <th style={{width: '100%'}}>
                        <VisuallyHidden>Item</VisuallyHidden>
                      </th>
                      <VisuallyHidden as='th'>Store</VisuallyHidden>
                      <th>Quantity</th>
                      <th>Price</th>
                      <VisuallyHidden as='th'>Actions</VisuallyHidden>
                    </tr>
                  </VisuallyHidden>
                  <tbody>
                    {isLoading && (
                      <tr>
                        <td colSpan={5}>
                          <div className={style.SpinnerContainer}>
                            <DotLottieReact
                              src={spinnerLottie}
                              autoplay
                              loop
                              className={style.Spinner}
                              aria-label='Loading product...'
                              role='progressbar'
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                    {isEmpty && (
                      <tr>
                        <td colSpan={5}>
                          <div className={style.EmptyCart}>Your cart is empty</div>
                        </td>
                      </tr>
                    )}
                    {!isLoading && !isEmpty && storeDetails && (
                      <tr aria-hidden>
                        <td colSpan={5}>
                          <div className={style.RetailerContainer}>
                            <div>
                              <img src={storeDetails.logo_photos[0]} alt='' />
                              {storeDetails.name}
                            </div>
                            {foodCart?.final_quote.quote.delivery_time_min && (
                              <div className={style.DeliveryTime}>
                                <ClockSvg />
                                <div>Est. Delivery</div>
                                <div>
                                  {scheduleId ? (
                                    expectedDeliveryDateRelative
                                  ) : (
                                    <>
                                      {expectedDeliveryMinute} min
                                      {expectedDeliveryMinute === 1 ? '' : 's'}
                                      {` (${foodCart.final_quote.quote.expected_time_of_arrival})`}
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                            {error && <div className={style.StoreError}>{error.message}</div>}
                            {/* TODO: hide & show products */}
                            {/* <button tabIndex={-1}>
                            <ChevronDownSvg aria-hidden />
                          </button> */}
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* TODO: remove item animation */}
                    {!isLoading &&
                      !isEmpty &&
                      storeId &&
                      results.foodCartItems.map(item => {
                        return (
                          <FoodCartItemComponent
                            key={item.productId}
                            item={item}
                            isMobile={isMobile}
                            storeId={storeId}
                          />
                        )
                      })}
                  </tbody>
                </table>
              </div>
              {!isMobile && <FoodCartSummary />}
            </div>
          </div>
        </main>
        {isMobile && <FoodCartSummary />}
        <Footer />
      </div>
    </>
  )
})
